import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../config/axiosInstance';
import { Table, Button, Space, message, Tooltip, Popconfirm, Tag, Col, Row } from 'antd';
import { EditOutlined, DeleteOutlined, MobileOutlined, EyeOutlined, EyeInvisibleOutlined, PlusOutlined,
    DesktopOutlined, RocketOutlined, MessageOutlined, QrcodeOutlined, SkinOutlined, WifiOutlined } from '@ant-design/icons';
import DeviceConfigEditForm from './DeviceConfigEditFormContainer';
import TermsModal from './TermsModal';
import DeviceTransferModal from './DeviceTransferModal';
import DeviceConfigPreview from './DeviceConfigPreview';
import SendMessagePopup from './SendMessagePopup';
import { useAuth } from '../../context/AuthContext';

const DeviceConfigList = () => {
  const getUniqueValues = (keyPath) => {
    const values = deviceConfigs.map(item => {
      const keys = keyPath.split('.');
      return keys.reduce((acc, key) => acc?.[key], item);
    }).filter(Boolean);

    return [...new Set(values)].map(value => ({
      text: value,
      value: value,
    }));
  };
  const { hasFeature } = useAuth();
  const [deviceConfigs, setDeviceConfigs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDeviceConfig, setEditingDeviceConfig] = useState(null);
  const [showVisible, setShowVisible] = useState(true);
  const [sendMessagePopupRecord, setSendMessagePopupRecord] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);           // controls TermsModal

  const fetchDeviceConfigs = useCallback(async () => {
    try {
      const response = await axios.get(`/deviceConfigs`);
      const filteredDeviceConfigs = response.data.filter(deviceConfig => deviceConfig.IsVisible === showVisible);
      setDeviceConfigs(filteredDeviceConfigs);
    } catch (error) {
      console.error('Error fetching deviceConfigs:', error);
    }
  }, [showVisible]);

  useEffect(() => {
    fetchDeviceConfigs();
  }, [fetchDeviceConfigs]);

  const handleDelete = async (record) => {
    try {
      await axios.delete(`/deviceConfigs/${record._id}`);
      setDeviceConfigs(prevDeviceConfigs =>
        prevDeviceConfigs.filter(deviceConfig => deviceConfig._id !== record._id)
      );
      message.success(`DeviceConfig ${record.DeviceConfigName} deleted successfully.`);
    } catch (error) {
      message.error('Error deleting DeviceConfig. Please try again.');
    }
  };

  const handleEdit = (record) => {
    setEditingDeviceConfig(record);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingDeviceConfig(null);
    setIsModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false); // Close the modal
    setEditingDeviceConfig(null); // Reset editing config
  };

  const handleToggleVisibility = async (record) => {
    try {
      const updatedRecord = { ...record, IsVisible: !record.IsVisible };
      await axios.put(`/deviceConfigs/${record._id}`, updatedRecord);
      setDeviceConfigs(prevDeviceConfigs =>
        prevDeviceConfigs.map(deviceConfig =>
          deviceConfig._id === record._id ? updatedRecord : deviceConfig
        )
      );
      fetchDeviceConfigs();
      message.success(`DeviceConfig ${updatedRecord.DeviceConfigName} visibility updated.`);
    } catch (error) {
      message.error('Error updating visibility. Please try again.');
    }
  };

  const handleToggleVisibilityFilter = () => {
    setShowVisible(!showVisible);
  };

  const handleDevices = (record) => {
    setEditingDeviceConfig(record);
    if (record.termsAccepted) {
      setIsTransferModalOpen(true);          // user already accepted
    } else {
      // First time: behavior depends on client.billable feature
      if (hasFeature('client.billable')) {
        setTermsOpen(true);       // Show terms section
      } else {
        setIsTransferModalOpen(true); // Open transfer modal directly
      }

    }
  };

  const handleTransferSuccess = () => {
    setIsTransferModalOpen(false);
    fetchDeviceConfigs();
  };

  const handleTermsSuccess = useCallback(() => {
    setTermsOpen(false);
    setIsTransferModalOpen(true);
    fetchDeviceConfigs();
  }, [fetchDeviceConfigs]);

  const handleUpdateConfig = async (record) => {
    try {
      const predefinedMessage = "This is a test message for online devices.";
      let DeviceConfigId= record._id;
      // Call to the backend to send the message
      await axios.post('/ws/update-config', {
        DeviceConfigId,
        message: predefinedMessage,
      });

      message.success(`Messages sent to online devices for Configuration: ${record.DeviceConfigName}`);
    } catch (error) {
      message.error(`Error sending message: ${record.DeviceConfigName}`);
    }
  };

  const handleSendMessage = (record) => {
    setSendMessagePopupRecord({ ...record, type: 'deviceConfig' });
  };

  const handlePreview = (record) => {
    setEditingDeviceConfig(record);
    setIsPreviewModalOpen(true);
  };

  const handlePreviewModalCancel = () => {
    setIsPreviewModalOpen(false);
    setEditingDeviceConfig(null);
  };


  const columns = [
    {
      title: 'Type',
      dataIndex: 'Type',
      key: 'Type',
      render: (type, record) => {
        const baseStyle = { fontSize: 18 };
        console.log('record.mode:', record?.Mode);
        const typeIcon =
            type === 'mobile' ? <MobileOutlined style={{ ...baseStyle, color: '#52c41a' }} /> :
                type === 'desktop' ? <DesktopOutlined style={{ ...baseStyle, color: '#0078D6' }} /> :
                    type === 'leadcapture' ? <QrcodeOutlined style={{ ...baseStyle, color: '#722ED1' }} /> :
                        type === 'entranceControl' ? <QrcodeOutlined style={{ ...baseStyle, color: '#722ED1' }} /> :
                            null;

        const modeIcon =
            type === 'mobile' && record?.Mode === 'cloakroom'
                ? <SkinOutlined style={{ ...baseStyle, marginLeft: 8 }} />
                : type === 'mobile' && record?.Mode === 'nfcLink'
                    ? <WifiOutlined style={{ ...baseStyle, marginLeft: 8 }} />
                    : null;

        return (
            <Space size={6}>
              {typeIcon}
              {modeIcon}
            </Space>
        );
      },
    },
    {
      title: 'Mnemonic',
      dataIndex: ['SelectedEventId', 'mnemonic'],
      key: 'mnemonic',
      filters: getUniqueValues('SelectedEventId.mnemonic'),
      onFilter: (value, record) =>
          record.SelectedEventId?.mnemonic?.toLowerCase().includes(value.toLowerCase()),
      filterSearch: true,
      sorter: (a, b) => {
        const nameA = a.SelectedEventId?.mnemonic?.toLowerCase() || '';
        const nameB = b.SelectedEventId?.mnemonic?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      },
      render: (text) => (
          <Tag color={text ? 'blue' : 'red'}>
            {text || 'Not Configured'}
          </Tag>
      ),
    },
    {
      title: 'Device Config Name',
      dataIndex: 'DeviceConfigName',
      key: 'DeviceConfigName',
      sorter: (a, b) => {
        const nameA = a.DeviceConfigName?.toLowerCase() || '';
        const nameB = b.DeviceConfigName?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">

          <Tooltip title="Devices" color="purple">
            <Button icon={<MobileOutlined style={{ color: 'skyblue' }}/>} onClick={() => handleDevices(record)} />
          </Tooltip>
          <Tooltip title={record.Type === 'desktop' ? "Preview" : ""} color="orange">
            <Button
                icon={<DesktopOutlined style={{ color: 'orange' }} />}
                onClick={() => record.Type === 'desktop' && handlePreview(record)}
                disabled={record.Type !== 'desktop'}
                style={{
                  visibility: record.Type === 'desktop' ? 'visible' : 'hidden',
                }}
            />
          </Tooltip>

          <Tooltip title="Update devices Configuration" color="orangered">
            <Button
              icon={<RocketOutlined style={{ color: 'orangered' }}/>}
              onClick={() => handleUpdateConfig(record)}
              style={{ marginLeft: '20px' }}
            />
          </Tooltip>
          <Tooltip title="Send message" color="green">
            <Button
                icon={<MessageOutlined style={{ color: 'green' }} />}
                onClick={() => handleSendMessage(record)}
            />
          </Tooltip>

          <Tooltip title="Edit" color="blue">
            <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} style={{ marginLeft: '20px' }} />
          </Tooltip>
          <Tooltip title={record.IsVisible ? "Visible" : "Invisible"} color={record.IsVisible ? "green" : "red"}>
            <Button
              icon={record.IsVisible ? <EyeOutlined /> : <EyeInvisibleOutlined style={{ color: 'red' }} />}
              onClick={() => handleToggleVisibility(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this device config?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete" color="red">
              <Button icon={<DeleteOutlined style={{ color: 'red' }}/>} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px'}}>
      <h1 style={{ marginBottom: '24px' }}>Device Configs List</h1>
      <Row Row gutter={[16, 16]} align="middle" style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={12} md={6} lg={4}>
          <Button
            icon={showVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={handleToggleVisibilityFilter}
            style={{ width: '100%' }}
          >
            {showVisible ? 'Show Hidden' : 'Show Visible'}
          </Button>
        </Col>
        <Col xs={12} sm={12} md={6} lg={4}>
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={handleAdd}
            style={{ width: '100%' }}
          >
            <span className="hide-text-mobile">Add</span>
          </Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={deviceConfigs}
        rowKey="_id"
        scroll={{ x: 'max-content' }}
      />

      {/* Modal for Editing and Adding DeviceConfig */}
      <DeviceConfigEditForm
        open={isModalOpen}
        initialValues={editingDeviceConfig || {}}
        onCancel={handleModalCancel}
        onFinish={fetchDeviceConfigs}
      />

      {/* Other Modals */}
      <TermsModal
          open={termsOpen}
          deviceConfigId={editingDeviceConfig?._id}
          onSuccess={handleTermsSuccess}
          onCancel={() => setTermsOpen(false)}
      />

      <DeviceTransferModal
        open={isTransferModalOpen}
        onCancel={() => setIsTransferModalOpen(false)}
        deviceConfig={editingDeviceConfig}
        onTransferSuccess={handleTransferSuccess}
      />

      <DeviceConfigPreview
          open={isPreviewModalOpen}
          onCancel={handlePreviewModalCancel}
          deviceConfig={editingDeviceConfig}
      />

      <SendMessagePopup
          open={!!sendMessagePopupRecord}
          record={sendMessagePopupRecord}
          onClose={() => setSendMessagePopupRecord(null)}
      />

    </div>
  );
};

export default DeviceConfigList;
